// Icônes inline des réponses de la FAQ (page Support & Aide).
//
// Le PDF « FAQ ARGOS » intercale des icônes dans le texte des réponses
// (« cliquez sur l’icône ⚙ »). Les réponses de faqData.js sont de simples
// chaînes : on y place donc des jetons `{{nom}}` que renderFaqAnswer()
// remplace par l’icône correspondante au rendu.
//
// Chaque icône reprend celle réellement affichée par ARGOS, pour que
// l’utilisateur retrouve exactement le même visuel dans l’application.

import React from 'react';
import { BiArrowFromLeft } from 'react-icons/bi';
import { ImEye, ImEyeBlocked } from 'react-icons/im';
import { VscSettingsGear } from 'react-icons/vsc';

import favoriOff from '../../ressources/accueil/fOff.png';
import favoriOn from '../../ressources/accueil/fOn.png';
import styles from './Formation.module.scss';

// Icônes de la palette d’outils des graphes. Elles sont définies comme chemins
// SVG dans BibliothequePlotly.js (variables locales, non exportées) ; on les
// redéfinit ici plutôt que d’importer ce module, qui embarquerait tout
// react-plotly.js dans le bundle de la page Support & Aide.
const OUTILS_GRAPHE = {
  // BibliothequePlotly.js > modifier_intervalle (icône Bootstrap 16x16).
  intervalle: {
    viewBox: '0 0 16 16',
    path: 'M1 0a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h5v-1H2v-1h4v-1H4v-1h2v-1H2v-1h4V9H4V8h2V7H2V6h4V2h1v4h1V4h1v2h1V2h1v4h1V4h1v2h1V2h1v4h1V1a1 1 0 0 0-1-1H1z',
  },
  // BibliothequePlotly.js > Expor_CSV (icône Bootstrap 16x16).
  csv: {
    viewBox: '0 0 16 16',
    path: 'M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0zM9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1zm-1 4v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 11.293V7.5a.5.5 0 0 1 1 0z',
  },
  // BibliothequePlotly.js > cameraIcon (bouton toImage natif de Plotly).
  photo: {
    viewBox: '0 0 1000 1000',
    transform: 'matrix(1 0 0 -1 0 850)',
    path: 'm500 450c-83 0-150-67-150-150 0-83 67-150 150-150 83 0 150 67 150 150 0 83-67 150-150 150z m400 150h-120c-16 0-34 13-39 29l-31 93c-6 15-23 28-40 28h-340c-16 0-34-13-39-28l-31-94c-6-15-23-28-40-28h-120c-55 0-100-45-100-100v-450c0-55 45-100 100-100h800c55 0 100 45 100 100v450c0 55-45 100-100 100z m-400-550c-138 0-250 112-250 250 0 138 112 250 250 250 138 0 250-112 250-250 0-138-112-250-250-250z m365 380c-19 0-35 16-35 35 0 19 16 35 35 35 19 0 35-16 35-35 0-19-16-35-35-35z',
  },
};

const IconeOutilGraphe = ({ nom }) => {
  const { viewBox, path, transform } = OUTILS_GRAPHE[nom];
  return (
    <svg
      className={styles.faqIcon}
      viewBox={viewBox}
      focusable="false"
      aria-hidden="true"
    >
      <path d={path} transform={transform} fill="currentColor" />
    </svg>
  );
};

// Jeton `{{nom}}` -> élément rendu dans la réponse.
const FAQ_ICONS = {
  // Menu de gauche : roue crantée des paramètres (cf. EnTeteParametre).
  engrenage: <VscSettingsGear className={styles.faqIcon} aria-hidden="true" />,
  // Bouton d’ouverture / fermeture du menu latéral (cf. Menu.js).
  menu: <BiArrowFromLeft className={styles.faqIcon} aria-hidden="true" />,
  // Épingle « favoris » : contour = à ajouter, jaune pleine = déjà en favori.
  'favori-off': (
    <img className={styles.faqIcon} src={favoriOff} alt="" aria-hidden="true" />
  ),
  'favori-on': (
    <img className={styles.faqIcon} src={favoriOn} alt="" aria-hidden="true" />
  ),
  // Palette d’outils des graphes.
  intervalle: <IconeOutilGraphe nom="intervalle" />,
  csv: <IconeOutilGraphe nom="csv" />,
  photo: <IconeOutilGraphe nom="photo" />,
  // Alarmes : œil ouvert = active/visible, œil barré = prise en compte
  // (cf. TableauAlarmesClient.js).
  oeil: <ImEye className={styles.faqIcon} aria-hidden="true" />,
  'oeil-barre': <ImEyeBlocked className={styles.faqIcon} aria-hidden="true" />,
};

const JETON = /\{\{([a-z-]+)\}\}/g;

/**
 * Découpe une réponse de FAQ et remplace les jetons `{{nom}}` par leur icône.
 * Un jeton inconnu est laissé tel quel, pour rester visible plutôt que de
 * disparaître silencieusement du texte.
 * @param {string} texte - Réponse déjà traduite par checkMot().
 * @returns {Array<string|React.ReactElement>} Contenu prêt à être rendu.
 */
export const renderFaqAnswer = (texte) => {
  if (typeof texte !== 'string') {
    return texte;
  }

  const parties = [];
  let precedent = 0;
  let occurrence;

  JETON.lastIndex = 0;
  while ((occurrence = JETON.exec(texte)) !== null) {
    if (occurrence.index > precedent) {
      parties.push(texte.slice(precedent, occurrence.index));
    }
    const icone = FAQ_ICONS[occurrence[1]];
    parties.push(
      icone
        ? React.cloneElement(icone, {
            key: `${occurrence[1]}-${occurrence.index}`,
          })
        : occurrence[0]
    );
    precedent = occurrence.index + occurrence[0].length;
  }
  if (precedent < texte.length) {
    parties.push(texte.slice(precedent));
  }

  return parties;
};

/**
 * Retire les jetons d’icône d’une réponse, pour que la recherche de la FAQ
 * porte sur le seul texte lisible (sans quoi « csv » ou « menu » matcherait le
 * nom d’un jeton autant que le contenu).
 * @param {string} texte - Réponse brute.
 * @returns {string} Texte sans jeton.
 */
export const stripFaqTokens = (texte) =>
  typeof texte === 'string' ? texte.replace(JETON, '') : '';
